import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analysisApi, exportApi } from '../services/api';

const HistoryPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['history', page],
    queryFn: () => analysisApi.getHistory(page, 10).then((r) => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => analysisApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history'] });
      setSelectedId(null);
    },
  });

  const handleExport = async (id: number, format: 'txt' | 'docx' | 'pdf') => {
    try {
      let response;
      if (format === 'txt') response = await exportApi.exportTxt(id);
      else if (format === 'docx') response = await exportApi.exportDocx(id);
      else response = await exportApi.exportPdf(id);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analysis_${id}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Export failed');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">History</h1>
        <p className="text-gray-500 dark:text-gray-400">Your previous analyses</p>
      </div>

      {data?.items?.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No analyses yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data?.items?.map((analysis: any) => (
            <div key={analysis.id} className="card">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() => setSelectedId(selectedId === analysis.id ? null : analysis.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white truncate">
                      {analysis.original_text.substring(0, 100)}...
                    </p>
                    <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <span>{new Date(analysis.created_at).toLocaleString()}</span>
                      <span>{analysis.edit_count} corrections</span>
                      <span>{analysis.provider_used}</span>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transform transition-transform ${
                      selectedId === analysis.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {selectedId === analysis.id && (
                <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Original</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-3 rounded">
                        {analysis.original_text}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Corrected</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-3 rounded">
                        {analysis.corrected_text}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleExport(analysis.id, 'txt')}
                        className="btn-ghost text-sm"
                      >
                        TXT
                      </button>
                      <button
                        onClick={() => handleExport(analysis.id, 'docx')}
                        className="btn-ghost text-sm"
                      >
                        DOCX
                      </button>
                      <button
                        onClick={() => handleExport(analysis.id, 'pdf')}
                        className="btn-ghost text-sm"
                      >
                        PDF
                      </button>
                    </div>
                    <button
                      onClick={() => deleteMutation.mutate(analysis.id)}
                      className="btn-ghost text-sm text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data?.total > 10 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Page {page} of {Math.ceil(data.total / 10)}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!data.has_more}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
